import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import { FiCheckCircle, FiHome, FiPackage, FiAlertCircle } from 'react-icons/fi';
import { supabase } from '../config/supabase';
import { useOrders } from '../hooks/useOrders';
import { formatOrderReference, orderConfirmationQueryFromParam } from '../utils/orderReference';

/** Dedupe auto-send across React Strict Mode’s double effect (same tick). */
const confirmationEmailInFlight = new Set();

const Container = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 40px 20px;
  min-height: 80vh;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Card = styled.div`
  background: white;
  border-radius: 16px;
  padding: 48px 32px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  text-align: center;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const IconWrapper = styled.div`
  width: 80px;
  height: 80px;
  background: #5efba9;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 24px;
`;

const StyledCheckCircle = styled(FiCheckCircle)`
  width: 48px;
  height: 48px;
  color: white;
`;

const StyledPackage = styled(FiPackage)`
  width: 48px;
  height: 48px;
  color: white;
`;

const Title = styled.h1`
  font-size: 32px;
  font-weight: 700;
  color: #1f2937;
  margin-bottom: 16px;
  text-align: center;
`;

const Message = styled.p`
  font-size: 18px;
  color: #6b7280;
  margin-bottom: 8px;
  text-align: center;
  max-width: 600px;
`;

const OrderNumber = styled.p`
  font-size: 16px;
  color: #374151;
  margin-bottom: 24px;
  font-weight: 500;
  text-align: center;
`;

const EmailSection = styled.div`
  width: 100%;
  max-width: 560px;
  margin-top: 8px;
  text-align: left;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 16px;
  justify-content: center;
  margin-top: 24px;
  flex-wrap: wrap;
`;

const Button = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 14px 28px;
  font-size: 16px;
  font-weight: 600;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;
`;

const PrimaryButton = styled(Button)`
  background: #5efba9;
  color: #374151;

  &:hover {
    background: #4ce89a;
  }
`;

const SecondaryButton = styled(Button)`
  background: #f3f4f6;
  color: #374151;

  &:hover {
    background: #e5e7eb;
  }
`;

const spin = keyframes`
  to {
    transform: rotate(360deg);
  }
`;

const GateSpinner = styled.div`
  width: 48px;
  height: 48px;
  margin: 8px auto 28px;
  border: 4px solid #d1fae5;
  border-top-color: #059669;
  border-radius: 50%;
  animation: ${spin} 0.85s linear infinite;
`;

const EmailStatusBanner = styled.div`
  width: 100%;
  max-width: 560px;
  margin-bottom: 14px;
  padding: 12px 14px;
  border-radius: 8px;
  font-size: 14px;
  line-height: 1.45;
  display: flex;
  align-items: flex-start;
  gap: 10px;
  text-align: left;
  border: 1px solid ${(p) => p.$border || '#e5e7eb'};
  background: ${(p) => p.$bg || '#f9fafb'};
  color: #1f2937;
`;

const EmailStatusTitle = styled.strong`
  display: block;
  margin-bottom: 2px;
  font-weight: 600;
`;

const GateNote = styled.p`
  font-size: 14px;
  color: #6b7280;
  max-width: 420px;
  margin: 0 auto 8px;
  line-height: 1.5;
`;

const ErrorBanner = styled.div`
  width: 100%;
  max-width: 560px;
  margin-bottom: 16px;
  padding: 12px 14px;
  border-radius: 8px;
  background: #fef2f2;
  border: 1px solid #fecaca;
  color: #991b1b;
  font-size: 14px;
  text-align: center;
`;

function OrderConfirmation() {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const { resendOrderConfirmationEmails } = useOrders();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [autoEmailStatus, setAutoEmailStatus] = useState('idle');

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      if (!orderId) {
        setLoading(false);
        setOrder(null);
        setLoadError(null);
        return;
      }

      setLoading(true);
      setOrder(null);
      setLoadError(null);
      setAutoEmailStatus('idle');

      try {
        const q = orderConfirmationQueryFromParam(orderId);
        if (!q) {
          setLoading(false);
          setLoadError('Invalid order link.');
          return;
        }
        const { data, error } = await supabase
          .from('orders')
          .select(`
            *,
            order_items:order_items(*)
          `)
          .eq(q.column, q.value)
          .single();

        if (error) throw error;
        if (cancelled) return;

        setOrder(data);

        if (data.payment_status === 'paid') {
          try {
            if (sessionStorage.getItem(`wasl_confirm_emails_ok_${data.id}`) === '1') {
              setAutoEmailStatus('done');
            } else {
              setAutoEmailStatus('sending');
            }
          } catch (_e) {
            setAutoEmailStatus('sending');
          }
        } else {
          setAutoEmailStatus('idle');
        }
      } catch (error) {
        console.error('Error fetching order:', error);
        if (!cancelled) {
          setLoadError(error?.message || 'Could not load this order.');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [orderId]);

  useEffect(() => {
    if (!order?.id || loading || order.payment_status !== 'paid') {
      return undefined;
    }

    const okKey = `wasl_confirm_emails_ok_${order.id}`;
    try {
      if (sessionStorage.getItem(okKey) === '1') {
        setAutoEmailStatus('done');
        return undefined;
      }
    } catch (_e) {
      /* ignore */
    }

    if (confirmationEmailInFlight.has(order.id)) {
      return undefined;
    }
    confirmationEmailInFlight.add(order.id);

    setAutoEmailStatus('sending');

    (async () => {
      try {
        const result = await resendOrderConfirmationEmails(order.id, { waitForPaidRow: true });
        if (result?.ok) {
          try {
            sessionStorage.setItem(okKey, '1');
          } catch (_e) {
            /* ignore */
          }
          setAutoEmailStatus('done');
        } else {
          setAutoEmailStatus('error');
        }
      } catch (e) {
        console.error('Automatic confirmation emails failed:', e);
        setAutoEmailStatus('error');
      } finally {
        confirmationEmailInFlight.delete(order.id);
      }
    })();
  }, [order?.id, order?.payment_status, loading, resendOrderConfirmationEmails]);

  if (!loading && (loadError || !order)) {
    return (
      <Container>
        <Card>
          <Title>Order</Title>
          <ErrorBanner>{loadError || 'Order not found.'}</ErrorBanner>
          <ButtonGroup>
            <PrimaryButton onClick={() => navigate('/')}>
              <FiHome /> Back home
            </PrimaryButton>
          </ButtonGroup>
        </Card>
      </Container>
    );
  }

  const isPaid = order?.payment_status === 'paid';
  const emailSending = Boolean(isPaid && autoEmailStatus === 'sending');
  const pageBlocking = loading || emailSending;
  const showPaidDetails = isPaid && (autoEmailStatus === 'done' || autoEmailStatus === 'error');

  if (pageBlocking) {
    return (
      <Container>
        <Card>
          {loading ? (
            <>
              <IconWrapper>
                <StyledPackage />
              </IconWrapper>
              <Title>Loading your order</Title>
              <Message>Please keep this page open while we load your confirmation.</Message>
              <GateSpinner role="progressbar" aria-label="Loading order" />
              <GateNote>This usually takes a few seconds — do not close this tab yet.</GateNote>
            </>
          ) : (
            <>
              <IconWrapper>
                <StyledCheckCircle />
              </IconWrapper>
              <Title>Order successful!</Title>
              <Message>Your payment was received. Please keep this page open while we send your confirmation.</Message>
              <OrderNumber>Order #{formatOrderReference(order)}</OrderNumber>
              <GateSpinner role="progressbar" aria-label="Sending confirmation email" />
              <GateNote>
                Sending your order confirmation and store notifications. This usually takes a few seconds — do not
                close this tab yet.
              </GateNote>
            </>
          )}
        </Card>
      </Container>
    );
  }

  return (
    <Container>
      <Card>
        <IconWrapper>
          {isPaid ? <StyledCheckCircle /> : <StyledPackage />}
        </IconWrapper>

        <Title>{isPaid ? 'Order successful!' : 'Payment pending'}</Title>
        <Message>
          {isPaid
            ? 'Thank you for your purchase. Your order has been placed successfully.'
            : 'Your order was created but payment is not complete yet. Complete payment to confirm your order.'}
        </Message>

        <OrderNumber>Order #{formatOrderReference(order)}</OrderNumber>

        {!isPaid && (
          <Message style={{ marginTop: 4, fontSize: '16px' }}>
            Complete payment to confirm your order. You will receive a confirmation email after payment succeeds.
          </Message>
        )}

        {(!isPaid || showPaidDetails) && (
          <EmailSection>
            {isPaid && autoEmailStatus === 'done' && (
              <EmailStatusBanner $bg="#ecfdf5" $border="#6ee7b7">
                <FiCheckCircle size={20} color="#059669" style={{ flexShrink: 0, marginTop: 2 }} aria-hidden />
                <div>
                  <EmailStatusTitle>Confirmation sent</EmailStatusTitle>
                  If you do not see the email in your inbox, check your spam folder or contact support.
                </div>
              </EmailStatusBanner>
            )}
            {isPaid && autoEmailStatus === 'error' && (
              <EmailStatusBanner $bg="#fef2f2" $border="#fecaca">
                <FiAlertCircle size={20} color="#dc2626" style={{ flexShrink: 0, marginTop: 2 }} aria-hidden />
                <div>
                  <EmailStatusTitle>We could not send the confirmation email</EmailStatusTitle>
                  Please contact support if you need a copy of your confirmation.
                </div>
              </EmailStatusBanner>
            )}
          </EmailSection>
        )}

        <ButtonGroup>
          {isPaid ? (
            <PrimaryButton onClick={() => navigate('/')} disabled={!showPaidDetails}>
              <FiHome /> Continue shopping
            </PrimaryButton>
          ) : (
            <>
              <PrimaryButton onClick={() => navigate('/checkout')}>
                Complete payment
              </PrimaryButton>
              <SecondaryButton onClick={() => navigate('/')}>
                <FiHome /> Back home
              </SecondaryButton>
            </>
          )}
        </ButtonGroup>
      </Card>
    </Container>
  );
}

export default OrderConfirmation;
